import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ChevronLeft, Users } from "lucide-react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleLIFT } from "@/components/lift/ModuleTitleLIFT";

export default function ManagerHub() {
  const { cycleId } = useParams();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, r] = await Promise.all([
          api.get(`/lift/cycles/${cycleId}`),
          api.get(`/lift/my-reports/${cycleId}`),
        ]);
        setCycle(c.data);
        setReports(r.data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, [cycleId]);

  if (loading || !cycle) return <p className="italic text-charcoal-muted">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="lift-manager-hub">
      <Link to="/lift" className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> LIFT home
      </Link>
      <div>
        <ModuleTitleLIFT />
        <h1 className="mt-3 text-4xl font-serif text-forest-900">Direct Reports · {cycle.name}</h1>
        <p className="mt-2 max-w-2xl text-charcoal-soft">
          For each teammate you shepherd this cycle: complete their evaluation, request peer voices, and finalize their packet.
        </p>
      </div>

      {reports.length === 0 ? (
        <div className="patria-card p-10 text-center">
          <Users className="h-8 w-8 mx-auto text-forest-600" strokeWidth={1.5} />
          <p className="mt-3 text-charcoal-soft">You currently have no direct reports in this cycle.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reports.map((r) => (
            <button
              key={r.employee_id}
              onClick={() => navigate(`/lift/manager/${cycleId}/${r.employee_id}`)}
              className="patria-card patria-card-hover p-6 text-left flex items-start gap-4"
              data-testid={`report-${r.employee_id}`}
            >
              {r.employee_avatar_path ? (
                <img
                  src={fileUrl(r.employee_avatar_path)}
                  alt={r.employee_name}
                  className="h-12 w-12 rounded-full object-cover"
                />
              ) : (
                <span className="h-12 w-12 rounded-full bg-forest-600/10 text-forest-700 flex items-center justify-center font-semibold">
                  {r.employee_name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </span>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-serif text-xl text-forest-900">{r.employee_name}</p>
                <p className="text-xs text-charcoal-muted">{r.employee_title || "—"}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={r.status_self_reflection === "submitted" ? "patria-chip-green" : "patria-chip-neutral"}>
                    Self · {r.status_self_reflection?.replace("_", " ")}
                  </span>
                  <span className={r.status_manager_evaluation === "submitted" ? "patria-chip-green" : "patria-chip-terra"}>
                    Manager · {r.status_manager_evaluation?.replace("_", " ")}
                  </span>
                  <span className="patria-chip-neutral">
                    Peers · {r.peer_responses_count}/{r.peer_requests_count}
                  </span>
                  {r.status_synthesis === "finalized" && <span className="patria-chip-green">Finalized</span>}
                  {r.status_synthesis === "acknowledged" && <span className="patria-chip-green">Acknowledged</span>}
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-forest-600 mt-2" strokeWidth={1.5} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
